import { Grid2Props as GridProps, InputAdornment } from '@mui/material'
import { DatePickerProps } from '@mui/x-date-pickers/DatePicker'
import { Dayjs } from 'dayjs'
import { Field } from './components/AccordionForm'
import type useMedicalInfo from './useMedicalInfo'
import { DateFieldKey, MedicalInfoFormKey } from './useMedicalInfo'

type MedicalInfoAccessors = Pick<
  ReturnType<typeof useMedicalInfo>,
  'getFieldControl' | 'getFieldValue' | 'setFieldValue'
>

export const DEFAULT_CONTAINER_PROPS: GridProps = {
  size: { xs: 12, sm: 6, md: 3 }
}

export const FULL_WIDTH_CONTAINER_PROPS: GridProps = {
  size: { xs: 12 }
}

export const YES_NO_BUTTON_LIST = [
  { value: true, label: 'Sim' },
  { value: false, label: 'Não' }
]

export const PAPER_FIELD_SX = {
  width: '100%',
  backgroundColor: 'background.paper'
}

const DATE_ADORNMENT_SLOT_PROPS: DatePickerProps<Dayjs, boolean>['slotProps'] = {
  inputAdornment: {
    position: 'start',
    sx: {
      color: 'primarySoft.main'
    }
  }
}

export function makeSwitchField<K extends MedicalInfoFormKey>(params: {
  title: string
  fieldKey: K
  id: string
  getFieldControl: MedicalInfoAccessors['getFieldControl']
  hidden?: boolean
  containerProps?: GridProps
}): Field {
  const { title, fieldKey, id, getFieldControl, hidden, containerProps } =
    params

  return {
    type: 'switch-btn',
    hidden,
    fieldProps: {
      title,
      control: getFieldControl(fieldKey),
      buttonList: YES_NO_BUTTON_LIST
    },
    containerProps: containerProps ?? DEFAULT_CONTAINER_PROPS,
    id
  }
}

export function makeDateField(params: {
  label: string
  fieldKey: DateFieldKey
  id: string
  getFieldValue: MedicalInfoAccessors['getFieldValue']
  setFieldValue: MedicalInfoAccessors['setFieldValue']
  containerProps?: GridProps
}): Field {
  const { label, fieldKey, id, getFieldValue, setFieldValue, containerProps } =
    params

  return {
    type: 'date',
    fieldProps: {
      label,
      value: getFieldValue(fieldKey),
      onChange: (val: Dayjs | null) => setFieldValue(fieldKey, val),
      sx: PAPER_FIELD_SX,
      slotProps: DATE_ADORNMENT_SLOT_PROPS
    },
    containerProps: containerProps ?? DEFAULT_CONTAINER_PROPS,
    id
  }
}

export function makeNumberField<K extends MedicalInfoFormKey>(params: {
  label: string
  fieldKey: K
  id: string
  unit: string
  mask: string
  getFieldControl: MedicalInfoAccessors['getFieldControl']
  containerProps?: GridProps
}): Field {
  const { label, fieldKey, id, unit, mask, getFieldControl, containerProps } =
    params

  return {
    type: 'number',
    fieldProps: {
      label,
      id: `${id}-input`,
      slotProps: {
        input: {
          endAdornment: <InputAdornment position='start'>{unit}</InputAdornment>
        }
      },
      control: getFieldControl(fieldKey),
      mask
    },
    containerProps: containerProps ?? DEFAULT_CONTAINER_PROPS,
    id
  }
}
